/**
 * Auth state and actions for the app. Mode differences live behind the `AuthStrategy` port
 * (services/auth); this context never branches on auth mode.
 */

import type { ReactNode } from "react"
import { createContext, useCallback, useContext, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { getAuthStrategy, type UserInfo } from "../services/auth"
import { ROUTES } from "../utils/constants"
import { consumeRedirectTarget } from "../utils/redirect"

interface AuthContextType {
  user: UserInfo | null
  loading: boolean
  login: (username: string, password: string) => Promise<void>
  loginWithRedirect: () => Promise<void>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
  /**
   * Complete a redirect sign-in on the callback route. A no-op in modes that sign in through
   * Amplify Hub.
   */
  completeRedirectLogin: () => Promise<void>
  /**
   * Redirect sign-in error, shown on the callback page.
   */
  redirectError: string | null
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider")
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<UserInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [redirectError, setRedirectError] = useState<string | null>(null)
  const navigate = useNavigate()
  const strategy = getAuthStrategy()

  const refreshUser = useCallback(async () => {
    try {
      setUser(await strategy.getCurrentUserInfo())
    } catch (error) {
      console.error("Error refreshing user:", error)
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [strategy])

  useEffect(() => {
    void refreshUser()
  }, [refreshUser])

  // Land on the page the user asked for before signing in, else the home route (which picks the
  // landing page from the user's access). Covers the federated Hub and direct-oidc callback paths.
  const handleSignedIn = useCallback(
    (userInfo: UserInfo) => {
      setUser(userInfo)
      navigate(consumeRedirectTarget() ?? ROUTES.HOME)
    },
    [navigate],
  )

  // Subscribe to auth lifecycle events (federated Hub sign-in, direct-oidc session loss).
  useEffect(() => {
    return strategy.subscribe({
      onSignedIn: handleSignedIn,
      onSignInError: setRedirectError,
      onSessionLost: () => {
        setUser(null)
        navigate(ROUTES.LOGIN)
      },
    })
  }, [strategy, handleSignedIn, navigate])

  const completeRedirectLogin = useCallback(async () => {
    try {
      const userInfo = await strategy.completeRedirectCallback()
      if (userInfo) {
        handleSignedIn(userInfo)
      }
    } catch (error) {
      console.error("Redirect login error:", error)
      setRedirectError(
        error instanceof Error ? error.message : "Sign-in did not complete. Please try again.",
      )
    }
  }, [strategy, handleSignedIn])

  const login = async (username: string, password: string) => {
    try {
      const userInfo = await strategy.login(username, password)
      setUser(userInfo)
      navigate(consumeRedirectTarget() ?? ROUTES.HOME)
    } catch (error) {
      console.error("Login error:", error)
      throw error
    }
  }

  const loginWithRedirect = async () => {
    try {
      await strategy.loginWithRedirect()
    } catch (error) {
      console.error("Redirect login error:", error)
      throw error
    }
  }

  const logout = async () => {
    try {
      await strategy.logout()
      setUser(null)
      setRedirectError(null)
      navigate(ROUTES.LOGIN)
    } catch (error) {
      console.error("Logout error:", error)
      throw error
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        loginWithRedirect,
        logout,
        refreshUser,
        completeRedirectLogin,
        redirectError,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
